// Per-route head management for the marketing site. The site is a single page
// app, so these write straight to the document: title, description, canonical,
// Open Graph and Twitter tags, and JSON-LD blocks that search engines and
// answer engines read for structured data. Each call replaces what the previous
// route set, so nothing leaks between pages.

export const SITE_URL = 'https://www.offthecouch.io';
export const SITE_NAME = 'Off The Couch';
export const DEFAULT_IMAGE = SITE_URL + '/img/otc-logo.png';

function upsertMeta(attr, key, content) {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!content) {
    if (el && el.dataset.managed) el.remove();
    return;
  }
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    el.dataset.managed = '1';
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function upsertCanonical(href) {
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

export function absoluteUrl(path) {
  if (!path) return DEFAULT_IMAGE;
  return path.startsWith('http') ? path : SITE_URL + path;
}

// { title, description, path, type ('website' | 'article'), image, publishedTime }
export function applyHead({ title, description, path, type = 'website', image, publishedTime }) {
  if (typeof document === 'undefined') return;
  const fullTitle = title ? `${title} | ${SITE_NAME}` : `${SITE_NAME}. The Venue Engine for booked experiences.`;
  const url = SITE_URL + (path || '/');
  const img = absoluteUrl(image);
  document.title = fullTitle;
  upsertMeta('name', 'description', description);
  upsertCanonical(url);
  upsertMeta('property', 'og:title', title || SITE_NAME);
  upsertMeta('property', 'og:description', description);
  upsertMeta('property', 'og:type', type);
  upsertMeta('property', 'og:url', url);
  upsertMeta('property', 'og:image', img);
  upsertMeta('property', 'og:site_name', SITE_NAME);
  upsertMeta('property', 'article:published_time', type === 'article' ? publishedTime : null);
  upsertMeta('name', 'twitter:card', 'summary_large_image');
  upsertMeta('name', 'twitter:title', title || SITE_NAME);
  upsertMeta('name', 'twitter:description', description);
  upsertMeta('name', 'twitter:image', img);
}

// Writes one JSON-LD block per id; a later call with the same id replaces it, null removes it.
export function setJsonLd(id, data) {
  if (typeof document === 'undefined') return;
  const key = `ld-${id}`;
  let el = document.getElementById(key);
  if (!data) {
    if (el) el.remove();
    return;
  }
  if (!el) {
    el = document.createElement('script');
    el.type = 'application/ld+json';
    el.id = key;
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

export function clearJsonLd(ids) {
  ids.forEach(id => setJsonLd(id, null));
}

export const ORGANIZATION = {
  '@type': 'Organization',
  name: SITE_NAME,
  url: SITE_URL,
  logo: { '@type': 'ImageObject', url: DEFAULT_IMAGE },
};
